import type { Task } from "@app/core";
import { isAncestor } from "@app/core";

import type { CoverRect } from "./cover-shape.js";

/**
 * Overlapping timed events render as a nested/cascading stack (not side-by-side
 * columns). Every event keeps its TRUE vertical time slot. A later event is
 * brought forward (higher z) and inset from the left — but only as far as needed
 * to avoid covering an *earlier* event's actual text:
 *   • if it starts within an ancestor's TITLE BAND, it insets past that title;
 *   • otherwise the ancestor's body is empty there, so it only leaves a ~10%
 *     strip of the ancestor visible and reclaims the rest of the width.
 * So a small event under the empty body of a big one expands left to show its
 * own title. When a cluster would overflow the column, all its insets are scaled
 * down together so every lane stays visible (never a full overlap).
 *
 * Pure + deterministic (title width is estimated from character count) — unit
 * tested against exact overlap shapes.
 */

const CHAR_PX = 6.2;
const LINE_PX = 15;
const MIN_LANE_W = 44;
/** Minimum offset between two overlapping lanes, px. */
const MIN_STEP_PX = 14;
/** Fraction of an ancestor's width left visible when a child slides left under
 *  its empty body (child covers ~90%). */
const STRIP_FRAC = 0.1;
/** Briefly clipping an ancestor's title should not force a long block into a
 *  half-width lane for its whole duration. */
const TITLE_COMPETE_FRAC = 0.25;
const LONG_AFTER_SHORT_RATIO = 3;
const MIN_CLEAR_PX = 40;
const MAX_TITLE_FRAC = 0.5;
const RIGHT_GAP_FRAC = 0.05;

export interface NestInput {
  task: Task;
  startMin: number;
  duration: number;
  isOverflow: boolean;
  overflowMinutes?: number;
}

export interface NestEntry {
  task: Task;
  topMin: number;
  heightMin: number;
  naturalStartMin: number;
  leftPx: number;
  zIndex: number;
  isOverflow: boolean;
  /** Bounding box (px, relative to this block's top-left) of the front block(s)
   *  that cover this one's right side, plus `rects` — one per covering block —
   *  used to carve the exact right-anchored staircase the content flows around
   *  (`cover-shape.ts`). Content flows into the free area beside each band and
   *  full-width in gaps/below. Undefined = nothing covers it. */
  cover?: { leftPx: number; topPx: number; bottomPx: number; rects: CoverRect[] };
}

interface Row {
  task: Task;
  start: number;
  end: number;
  isOverflow: boolean;
  startMin: number;
  lane: number;
  cluster: number;
  leftPx: number;
  clearWidth: number;
  bandMin: number;
}

const overlaps = (a: Row, b: Row) => a.start < b.end && b.start < a.end;

export function computeNestLayout(items: NestInput[], hourHeight: number, colWidthPx: number): NestEntry[] {
  const usable = colWidthPx * (1 - RIGHT_GAP_FRAC);
  const limit = Math.max(0, usable - MIN_LANE_W);

  // A subtask must nest UNDER its parent: when they overlap, the parent gets the
  // shallower lane (drawn behind/left) and the child insets in front of it.
  const taskList = items.map((it) => it.task);

  const rows: Row[] = items
    .map((it) => {
      const start = it.isOverflow ? 0 : it.startMin;
      const dur = it.isOverflow ? (it.overflowMinutes ?? it.duration) : it.duration;
      const estW = it.task.title.length * CHAR_PX;
      const clearWidth = Math.min(Math.max(estW / 4, MIN_CLEAR_PX), MAX_TITLE_FRAC * colWidthPx);
      const lines = Math.min(Math.max(Math.ceil(estW / Math.max(clearWidth, 1)), 1), 4);
      return {
        task: it.task, start, end: start + dur, isOverflow: it.isOverflow, startMin: it.startMin,
        lane: 0, cluster: 0, leftPx: 0, clearWidth, bandMin: ((lines * LINE_PX) / hourHeight) * 60,
      };
    })
    .sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      if (a.end !== b.end) return b.end - a.end; // longer first → backmost container
      if (a.task.id !== b.task.id) {
        // Parent before its descendant so the child insets under it.
        if (isAncestor(taskList, a.task.id, b.task.id)) return -1;
        if (isAncestor(taskList, b.task.id, a.task.id)) return 1;
      }
      return Number(b.task.is_event) - Number(a.task.is_event); // events outrank tasks
    });

  // Pass 1: clusters + lane (z) via interval colouring; peak lanes per cluster.
  let clusterEnd = -1;
  let cluster = -1;
  const lanes: number[] = [];
  const open1: Row[] = [];
  for (const r of rows) {
    if (r.start >= clusterEnd) { cluster++; clusterEnd = r.end; } else { clusterEnd = Math.max(clusterEnd, r.end); }
    r.cluster = cluster;
    for (let i = open1.length - 1; i >= 0; i--) if (open1[i]!.end <= r.start) open1.splice(i, 1);
    const used = new Set(open1.map((o) => o.lane));
    let lane = 0;
    while (used.has(lane)) lane++;
    r.lane = lane;
    open1.push(r);
    lanes[cluster] = Math.max(lanes[cluster] ?? 0, lane + 1);
  }

  // Pass 2: content-aware left inset. An ancestor whose TITLE BAND this block
  // starts within is genuinely competing for the top, so we give an EQUAL share
  // (usable / lanes) — no reason to cramp one. An ancestor whose body is empty
  // here (this block starts below its title band) only keeps a ~10% strip, so a
  // small block under a big one's empty body slides left to show its own title.
  const open2: Row[] = [];
  for (const r of rows) {
    for (let i = open2.length - 1; i >= 0; i--) if (open2[i]!.end <= r.start) open2.splice(i, 1);
    const equalStep = usable / (lanes[r.cluster] ?? 1);
    let leftPx = 0;
    for (const a of open2) {
      const bandOverlap = Math.max(0, Math.min(r.end, a.start + a.bandMin) - Math.max(r.start, a.start));
      const competesForTitle = r.start === a.start || bandOverlap / Math.max(1, r.end - r.start) >= TITLE_COMPETE_FRAC;
      if (competesForTitle) {
        leftPx = Math.max(leftPx, a.leftPx + equalStep);
      } else {
        const strip = Math.max(MIN_STEP_PX, STRIP_FRAC * (usable - a.leftPx));
        const longAfterShort = a.leftPx > 0 && (r.end - r.start) >= (a.end - a.start) * LONG_AFTER_SHORT_RATIO;
        if (longAfterShort) {
          leftPx = 0;
          continue;
        }
        leftPx = Math.max(leftPx, a.leftPx + strip);
      }
    }
    r.leftPx = leftPx;
    open2.push(r);
  }

  // Scale each cluster down if its deepest inset would overflow the column.
  const maxLeft: number[] = [];
  for (const r of rows) maxLeft[r.cluster] = Math.max(maxLeft[r.cluster] ?? 0, r.leftPx);
  for (const r of rows) {
    const m = maxLeft[r.cluster] ?? 0;
    if (m > limit) r.leftPx = (r.leftPx / m) * limit;
  }

  const rowIndex = new Map<Row, number>();
  rows.forEach((r, i) => rowIndex.set(r, i));
  const zIndexByRow = new Map<Row, number>();
  [...rows]
    .sort((a, b) => a.leftPx - b.leftPx || rowIndex.get(a)! - rowIndex.get(b)!)
    .forEach((r, i) => zIndexByRow.set(r, i));

  // For each block, the bounding box of the front block(s) covering its right
  // side — content flows around it. zIndex follows the final horizontal inset
  // so a block further right stays in front even when a later long block reclaims
  // left-side body space.
  const toPx = (min: number) => (min / 60) * hourHeight;
  return rows.map((r) => {
    let minLeft = Infinity, minTop = Infinity, maxBottom = -Infinity;
    const rects: CoverRect[] = [];
    for (const o of rows) {
      if (o === r || o.leftPx <= r.leftPx + 0.5 || !overlaps(r, o)) continue;
      minLeft = Math.min(minLeft, o.leftPx);
      minTop = Math.min(minTop, o.start);
      maxBottom = Math.max(maxBottom, o.end);
      rects.push({
        leftPx: o.leftPx - r.leftPx,
        topPx: Math.max(0, toPx(o.start - r.start)),
        bottomPx: toPx(Math.min(o.end, r.end) - r.start),
      });
    }
    const cover = Number.isFinite(minLeft)
      ? { leftPx: minLeft - r.leftPx, topPx: Math.max(0, toPx(minTop - r.start)), bottomPx: toPx(maxBottom - r.start), rects }
      : undefined;
    return {
      task: r.task,
      topMin: r.start,
      heightMin: r.end - r.start,
      naturalStartMin: r.startMin,
      leftPx: r.leftPx,
      zIndex: zIndexByRow.get(r) ?? 0,
      isOverflow: r.isOverflow,
      ...(cover ? { cover } : {}),
    };
  });
}
